// <soft-tabs> — tab toggle between server-rendered panels.
// <soft-slider> — scroll-snap carousel with pagination dots.
//
// Markup contract:
//   soft-tabs:   [data-tab="<id>"] buttons + [data-panel="<id>"] panels
//   soft-slider: [data-track] scroll container whose children are slides,
//                [data-dots] container (dots are generated)

class SoftTabs extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      const tab = event.target.closest('[data-tab]');
      if (tab) this.select(tab.dataset.tab);
    });
  }

  select(id) {
    for (const tab of this.querySelectorAll('[data-tab]')) {
      tab.setAttribute('aria-selected', tab.dataset.tab === id ? 'true' : 'false');
    }
    for (const panel of this.querySelectorAll('[data-panel]')) {
      panel.hidden = panel.dataset.panel !== id;
    }
  }
}

class SoftSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.dots = this.querySelector('[data-dots]');
    if (!this.track) return;

    this.buildDots();

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.setActiveDot([...this.track.children].indexOf(entry.target));
          }
        }
      },
      { root: this.track, threshold: 0.6 }
    );

    for (const slide of this.track.children) this.observer.observe(slide);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  buildDots() {
    if (!this.dots) return;

    this.dots.replaceChildren();
    const slides = [...this.track.children];
    if (slides.length < 2) return;

    slides.forEach((slide, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'sp-slider__dot';
      dot.setAttribute('aria-label', `Slide ${index + 1}`);
      dot.addEventListener('click', () => {
        slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      });
      this.dots.append(dot);
    });

    this.setActiveDot(0);
  }

  setActiveDot(index) {
    if (!this.dots) return;
    [...this.dots.children].forEach((dot, dotIndex) => {
      dot.toggleAttribute('data-active', dotIndex === index);
    });
  }
}

if (!customElements.get('soft-tabs')) customElements.define('soft-tabs', SoftTabs);
if (!customElements.get('soft-slider')) customElements.define('soft-slider', SoftSlider);
